/**
 * Embroidery machine catalogue: each model lists the hoops (sewing fields) it accepts, so the
 * hoop choice can be limited to what the machine can actually sew.
 *
 * Sizes are the sewing field (width × height as the manufacturer lists it), not the outer size
 * of the hoop. Hoop ids are the size ("130x180"), so a hoop shared by several machines, or by the
 * generic list, keeps its id when the machine is changed.
 *
 * Sources: manufacturer and dealer specifications (2026). Hoops included with a machine vary by
 * market and bundle; the lists below are the hoops the machine accepts.
 */
import { HOOPS } from './params';
import type { Hoop } from './types';

export interface MachineModel {
  id: string;
  brand: string;
  model: string;
  hoops: Hoop[];
  /** Hoop chosen when the machine is selected and the current hoop does not fit it. */
  defaultHoopId: string;
}

const hoop = (widthMm: number, heightMm: number, label?: string): Hoop => {
  const size = `${widthMm} × ${heightMm} mm`;
  return { id: `${widthMm}x${heightMm}`, name: label ? `${label} · ${size}` : size, widthMm, heightMm };
};

export const MACHINES: MachineModel[] = [
  {
    id: 'brother-se',
    brand: 'Brother',
    model: 'SE600 / SE630 / SE700 (4×4")',
    hoops: [hoop(20, 60, '1×2.5"'), hoop(100, 100, '4×4"')],
    defaultHoopId: '100x100',
  },
  {
    id: 'brother-pe',
    brand: 'Brother',
    model: 'PE800 / PE900 (5×7")',
    hoops: [hoop(20, 60, '1×2.5"'), hoop(100, 100, '4×4"'), hoop(130, 180, '5×7"')],
    defaultHoopId: '130x180',
  },
  {
    id: 'brother-nq1700e',
    brand: 'Brother',
    model: 'Innov-is NQ1700E (6×10")',
    hoops: [hoop(100, 100, '4×4"'), hoop(130, 180, '5×7"'), hoop(160, 260, '6×10"')],
    defaultHoopId: '160x260',
  },
  {
    id: 'brother-xj1',
    brand: 'Brother',
    model: 'Stellaire Innov-is XJ1',
    hoops: [hoop(100, 100, '4×4"'), hoop(130, 180, '5×7"'), hoop(160, 260, '6×10"'), hoop(240, 360, '9.5×14"')],
    defaultHoopId: '240x360',
  },
  {
    id: 'janome-mc500e',
    brand: 'Janome',
    model: 'Memory Craft 500E',
    hoops: [hoop(140, 140, 'SQ14b'), hoop(140, 200, 'RE20b'), hoop(200, 200, 'SQ20b'), hoop(200, 280, 'RE28b')],
    defaultHoopId: '200x280',
  },
  {
    id: 'bernina-7-8',
    brand: 'BERNINA',
    model: '7 / 8 Series (e.g. B 790 PLUS)',
    hoops: [hoop(100, 130, 'Medium'), hoop(145, 255, 'Large Oval'), hoop(165, 265, 'Midi'), hoop(210, 400, 'Maxi'), hoop(260, 400, 'Jumbo')],
    defaultHoopId: '145x255',
  },
  {
    id: 'husqvarna-epic3',
    brand: 'Husqvarna Viking',
    model: 'DESIGNER EPIC 3',
    hoops: [hoop(120, 120, 'Splendid Square II'), hoop(240, 150, 'Jewel II'), hoop(360, 200, 'Royal II'), hoop(465, 260, 'Regal')],
    defaultHoopId: '360x200',
  },
  {
    id: 'pfaff-icon2',
    brand: 'PFAFF',
    model: 'creative icon 2',
    hoops: [hoop(120, 120, 'creative 120 Square'), hoop(240, 150, 'creative Master'), hoop(360, 260, 'creative Icon'), hoop(360, 350, 'creative Grand Dream')],
    defaultHoopId: '360x260',
  },
];

export const machineById = (id: string | null | undefined): MachineModel | null => MACHINES.find((m) => m.id === id) ?? null;

/** Hoops offered for a machine; null (no machine chosen) offers the generic sizes. */
export const hoopsFor = (machineId: string | null | undefined): Hoop[] => machineById(machineId)?.hoops ?? HOOPS;

export const machineLabel = (m: MachineModel): string => `${m.brand} ${m.model}`;

/**
 * The hoop to use after switching machine: the current one if the machine accepts it (same
 * size), otherwise the machine's default. A custom size is kept when no machine is chosen.
 */
export function hoopForMachine(machineId: string | null, current: Hoop): Hoop {
  const list = hoopsFor(machineId);
  const same = list.find((h) => h.widthMm === current.widthMm && h.heightMm === current.heightMm);
  if (same) return same;
  const machine = machineById(machineId);
  if (!machine) return current;
  return machine.hoops.find((h) => h.id === machine.defaultHoopId) ?? machine.hoops[0];
}
